import React, { useState, useEffect, useCallback } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Plus, Search, Clock, ArrowLeft, Eye } from 'lucide-react'
import { format, parse, subMinutes, subHours } from 'date-fns'
import type { DateRange } from 'react-day-picker'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import RangeDayPicker from '@/components/ui/range-date-picker'
import { KeywordChip } from '@/components/scan'
import { useScanSubmit } from '@/lib/hooks/useScanSubmit'
import { scanFormSchema, type ScanFormData } from '@/lib/validation/schemas'
import type { TimeFramePreset } from '@/lib/types'
import { autoCorrectTime } from '@/lib/utils/timeFormat'
import { useCrisisStore } from '@/lib/stores/crisisStore'
import { validateCoostoQuery, type CoostoQueryError } from '@/lib/utils/coostoQuery'
import { useTranslation } from 'react-i18next'
import { scanService, type ScanQueryPreview } from '@/lib/services/scanService'

const timeFrameOptions: Array<{ value: TimeFramePreset; labelKey: string }> = [
  { value: 'last_15_minutes', labelKey: 'scan.timeFrames.last15Minutes' },
  { value: 'last_30_minutes', labelKey: 'scan.timeFrames.last30Minutes' },
  { value: 'last_1_hour', labelKey: 'scan.timeFrames.lastHour' },
  { value: 'custom', labelKey: 'scan.timeFrames.custom' },
]

interface ScanCreateModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess?: () => void
}

export function ScanCreateModal({ open, onOpenChange, onSuccess }: ScanCreateModalProps) {
  const [currentKeyword, setCurrentKeyword] = useState('')
  const [useCustomQuery, setUseCustomQuery] = useState(false)
  const [queryPreview, setQueryPreview] = useState<ScanQueryPreview['data'] | null>(null)
  const [isPreviewLoading, setIsPreviewLoading] = useState(false)
  const [previewError, setPreviewError] = useState<string | null>(null)
  const [range, setRange] = useState<DateRange | undefined>()
  const { activeCrisis } = useCrisisStore()
  const { t } = useTranslation()

  // Calculate default dates for custom time frame
  const now = new Date()
  const oneHourAgo = new Date(now.getTime() - 60 * 60 * 1000)

  // Format dates in dd/mm/yyyy format
  const defaultStartDate = format(oneHourAgo, 'dd/MM/yyyy')
  const defaultStartTime = format(oneHourAgo, 'HH:mm')
  const defaultEndDate = format(now, 'dd/MM/yyyy')
  const defaultEndTime = format(now, 'HH:mm')

  const { submitScan, isSubmitting, error } = useScanSubmit()

  // Generate default keywords from active crisis
  // First keyword is always the location (editable), other keywords are regular
  const getDefaultKeywords = useCallback(() => {
    if (!activeCrisis) return []

    const keywords = []
    // Add crisis type as the FIRST keyword (always editable)
    if (activeCrisis.type) {
      keywords.push(activeCrisis.type)
    }

    if (activeCrisis.location) {
      keywords.push(activeCrisis.location)
    }
    // Add crisis type as a keyword

    return keywords
  }, [activeCrisis])

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    reset,
  } = useForm<ScanFormData>({
    resolver: zodResolver(scanFormSchema),
    defaultValues: {
      keywords: getDefaultKeywords(),
      timeFrame: {
        preset: 'last_1_hour',
        fromDate: defaultStartDate,
        toDate: defaultEndDate,
        fromTime: defaultStartTime,
        toTime: defaultEndTime,
      },
    },
  })

  const keywords = watch('keywords') || []
  const keywordsKey = keywords.join('\u0000')
  const searchQuery = watch('searchQuery') || ''
  const timeFramePreset = watch('timeFrame.preset')
  const timeFrameFromDate = watch('timeFrame.fromDate')
  const timeFrameToDate = watch('timeFrame.toDate')
  const timeFrameFromTime = watch('timeFrame.fromTime')
  const timeFrameToTime = watch('timeFrame.toTime')
  const customQueryValidation = validateCoostoQuery(searchQuery)
  const customQueryError = customQueryValidation.message
    ? t(`scan.customQuery.errors.${customQueryValidation.message as CoostoQueryError}`)
    : undefined

  const highlightCoostoMatches = (text: string) =>
    text.split(/(\[[^\]]+\])/g).map((part, index) =>
      /^\[[^\]]+\]$/.test(part) ? (
        <mark key={`${part}-${index}`} className="rounded bg-orange-200 px-0.5 text-orange-950">
          {part}
        </mark>
      ) : (
        part
      )
    )

  useEffect(() => {
    setQueryPreview(null)
    setPreviewError(null)
  }, [
    keywordsKey,
    searchQuery,
    timeFramePreset,
    timeFrameFromDate,
    timeFrameToDate,
    timeFrameFromTime,
    timeFrameToTime,
    useCustomQuery,
  ])

  // Reset form when modal opens or closes
  useEffect(() => {
    if (!open) {
      // Reset form when closing
      reset()
      setCurrentKeyword('')
      setUseCustomQuery(false)
      setRange(undefined)
    } else {
      // Set default keywords when opening
      setValue('keywords', getDefaultKeywords())
    }
  }, [open, reset, activeCrisis, setValue, getDefaultKeywords])

  // Initialize custom date fields when preset changes to 'custom'
  useEffect(() => {
    if (timeFramePreset === 'custom') {
      const fromDate = parse(defaultStartDate, 'dd/MM/yyyy', new Date())
      const toDate = parse(defaultEndDate, 'dd/MM/yyyy', new Date())
      setRange({ from: fromDate, to: toDate })
      setValue('timeFrame.fromDate', defaultStartDate)
      setValue('timeFrame.toDate', defaultEndDate)
      setValue('timeFrame.fromTime', defaultStartTime)
      setValue('timeFrame.toTime', defaultEndTime)
    }
  }, [
    timeFramePreset,
    setValue,
    defaultStartDate,
    defaultEndDate,
    defaultStartTime,
    defaultEndTime,
  ])

  // Sync range to form values
  useEffect(() => {
    if (range?.from && range?.to) {
      setValue('timeFrame.fromDate', format(range.from, 'dd/MM/yyyy'))
      setValue('timeFrame.toDate', format(range.to, 'dd/MM/yyyy'))
    }
  }, [range, setValue])

  const addKeyword = (keyword: string) => {
    const trimmed = keyword.trim()
    if (trimmed && !keywords.includes(trimmed)) {
      setValue('keywords', [...keywords, trimmed])
      setCurrentKeyword('')
    }
  }

  const removeKeyword = (index: number) => {
    setValue(
      'keywords',
      keywords.filter((_: string, i: number) => i !== index)
    )
  }

  const editKeyword = (index: number, newValue: string) => {
    const trimmed = newValue.trim()
    if (trimmed && !keywords.includes(trimmed)) {
      const updatedKeywords = [...keywords]
      updatedKeywords[index] = trimmed
      setValue('keywords', updatedKeywords)
    }
  }

  const handleKeywordKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      addKeyword(currentKeyword)
    }
  }

  const handleTimeBlur =
    (field: 'timeFrame.fromTime' | 'timeFrame.toTime') =>
      (e: React.FocusEvent<HTMLInputElement>) => {
        const correctedTime = autoCorrectTime(e.target.value)
        if (correctedTime) {
          setValue(field, correctedTime)
        }
      }

  const onSubmit = async (data: ScanFormData) => {
    console.debug('Submitting scan with data:', data)

    if (useCustomQuery) {
      data.keywords = []
      data.searchQuery = searchQuery
    }

    // Convert preset timeframes to actual date ranges
    if (data.timeFrame.preset !== 'custom') {
      const now = new Date()
      let start: Date

      switch (data.timeFrame.preset) {
        case 'last_15_minutes':
          start = subMinutes(now, 15)
          break
        case 'last_30_minutes':
          start = subMinutes(now, 30)
          break
        case 'last_45_minutes':
          start = subMinutes(now, 45)
          break
        case 'last_1_hour':
          start = subHours(now, 1)
          break
        default:
          start = subHours(now, 1) // fallback to 1 hour
      }

      data.timeFrame.from = start
      data.timeFrame.to = now
    }

    const success = await submitScan(data)
    if (success) {
      onOpenChange(false)
      if (onSuccess) {
        onSuccess()
      }
    }
  }

  const isFormValid = useCustomQuery ? customQueryValidation.valid : keywords.length > 0

  const previewQuery = async () => {
    if (!isFormValid) return
    setIsPreviewLoading(true)
    setPreviewError(null)
    try {
      const response = await scanService.previewQuery({
        keywords: useCustomQuery ? [] : keywords,
        searchQuery: useCustomQuery ? searchQuery : undefined,
        timeFrame: watch('timeFrame'),
      } as ScanFormData)
      setQueryPreview(response.data)
    } catch (err) {
      setPreviewError(err instanceof Error ? err.message : t('scan.create.preview.error'))
      setQueryPreview(null)
    } finally {
      setIsPreviewLoading(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="md:max-w-7xl md:w-[95vw] max-h-[90vh] overflow-y-auto rounded-lg">
        <DialogHeader>
          <DialogTitle className="text-2xl">{t('scan.create.title')}</DialogTitle>
          <DialogDescription>
            {t('scan.create.description')}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Grid Layout for 2 blocks */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Keywords */}
            {!useCustomQuery && <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Search className="w-5 h-5" />
                  {t('scan.create.keywords.title')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="text-sm text-muted-foreground mb-4">
                    {t('scan.create.keywords.description')}
                  </p>

                  <div className="flex gap-2 mb-3 align-stretch">
                    <Input
                      value={currentKeyword}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setCurrentKeyword(e.target.value)
                      }
                      onKeyPress={handleKeywordKeyPress}
                      placeholder={t('scan.create.keywords.placeholder')}
                      className="flex-1"
                    />
                    <Button
                      type="button"
                      variant="gradient"
                      onClick={() => addKeyword(currentKeyword)}
                      disabled={!currentKeyword.trim()}
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>

                  {/* Current Keywords */}
                  {keywords.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {keywords.map((keyword: string, index: number) => {
                        // First keyword is always the location keyword (editable, not removable)
                        const isLocationKeyword = index === 0
                        return (
                          <KeywordChip
                            key={index}
                            keyword={keyword}
                            onRemove={isLocationKeyword ? undefined : () => removeKeyword(index)}
                            onEdit={
                              isLocationKeyword
                                ? newValue => editKeyword(index, newValue)
                                : undefined
                            }
                            variant={isLocationKeyword ? 'editable' : 'default'}
                          />
                        )
                      })}
                    </div>
                  ) : (
                    <p className="text-sm text-red-700 text-muted-foreground">
                      {t('scan.create.keywords.required')}
                    </p>
                  )}

                  {errors.keywords && (
                    <p className="text-sm text-destructive">{errors.keywords.message}</p>
                  )}

                  {/* Search Query Preview */}
                  {keywords.length > 0 && (
                    <div className="mt-4 p-3 bg-muted/50 rounded-md border border-border">
                      <Label className="text-xs font-semibold text-muted-foreground mb-1 block">
                        {t('scan.create.keywords.preview')}
                      </Label>
                      <code className="text-sm font-mono text-foreground">
                        {keywords.length === 1 ? (
                          `(${keywords[0]})`
                        ) : (
                          <>
                            ({keywords[0]}) AND ({keywords.slice(1).join(' OR ')})
                          </>
                        )}
                      </code>
                    </div>
                  )}

                  <Button
                    type="button"
                    variant="link"
                    className="h-auto ps-0 text-sm"
                    onClick={() => {
                      const nextUseCustomQuery = !useCustomQuery
                      setUseCustomQuery(nextUseCustomQuery)
                      if (!nextUseCustomQuery) setValue('searchQuery', '')
                    }}
                  >
                    {t('scan.customQuery.useCustom')}
                  </Button>
                </div>
              </CardContent>
            </Card>}

            {useCustomQuery && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex-col items-center justify-between text-lg">

                    <Button
                      type="button"
                      variant="link"
                      className="h-auto ps-0 text-sm"
                      onClick={() => {
                        setUseCustomQuery(false)
                        setValue('searchQuery', '')
                      }}
                    >
                      <ArrowLeft className="w-4 h-4 mr-1" />
                      {t('scan.customQuery.useGenerated')}
                    </Button>
                    <span>{t('scan.customQuery.title')}</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <Label htmlFor="custom-search-query">{t('scan.customQuery.label')}</Label>
                  <textarea
                    id="custom-search-query"
                    {...register('searchQuery')}
                    placeholder={t('scan.customQuery.placeholder')}
                    className="border-input bg-background ring-offset-background placeholder:text-muted-foreground focus-visible:ring-ring min-h-24 w-full rounded-md border px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
                    aria-invalid={!customQueryValidation.valid}
                  />
                  <p
                    className={`text-xs ${customQueryValidation.valid ? 'text-green-700' : 'text-destructive'}`}
                    role="status"
                  >
                    {customQueryValidation.valid
                      ? t('scan.customQuery.valid')
                      : customQueryError}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {t('scan.customQuery.help')}
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Time Frame */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Clock className="w-5 h-5" />
                  {t('scan.create.timeFrame.title')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-4">
                  {t('scan.create.timeFrame.description')}
                </p>
                <div className="space-y-3 mt-3">
                  {timeFrameOptions.map(option => (
                    <div key={option.value} className="space-y-3">
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="radio"
                          value={option.value}
                          {...register('timeFrame.preset')}
                          className="w-4 h-4 text-primary border-gray-300 focus:ring-primary"
                        />
                        <span className="text-sm font-medium">{t(option.labelKey)}</span>
                      </label>

                      {option.value === 'custom' && timeFramePreset === 'custom' && (
                        <div className="ml-6 space-y-3 p-4 bg-muted/30 rounded-md">
                          <div className="space-y-4">
                            <div className="space-y-1">
                              <RangeDayPicker value={range} onChange={setRange} />
                              <p className="text-xs text-muted-foreground italic">
                                {t('scan.create.timeFrame.maxDays')}
                              </p>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div className="space-y-2">
                                <Label htmlFor="from-time" variant="required">
                                  {t('scan.create.timeFrame.startTime')}
                                </Label>
                                <Input
                                  id="from-time"
                                  type="text"
                                  placeholder="HH:MM"
                                  {...register('timeFrame.fromTime')}
                                  onBlur={handleTimeBlur('timeFrame.fromTime')}
                                  className="w-full"
                                  maxLength={5}
                                />
                              </div>
                              <div className="space-y-2">
                                <Label htmlFor="to-time" variant="required">
                                  {t('scan.create.timeFrame.endTime')}
                                </Label>
                                <Input
                                  id="to-time"
                                  type="text"
                                  placeholder="HH:MM"
                                  {...register('timeFrame.toTime')}
                                  onBlur={handleTimeBlur('timeFrame.toTime')}
                                  className="w-full"
                                  maxLength={5}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {errors.timeFrame && (
                  <p className="text-sm text-destructive mt-1">{errors.timeFrame.message}</p>
                )}
              </CardContent>
            </Card>
          </div>

          <div className="space-y-3">
            <Button
              type="button"
              variant="outline"
              onClick={previewQuery}
              disabled={!isFormValid || isPreviewLoading}
              loading={isPreviewLoading}
            >
              {isPreviewLoading
                ? t('scan.create.preview.loading')
                : (<>
                  <Eye className="w-4 h-4 mr-2" />
                  {t('scan.create.preview.action')}
                </>
                )}
            </Button>
            {previewError && <p className="text-sm text-destructive">{previewError}</p>}
            {queryPreview && (
              <div className="space-y-3 rounded-md border border-border bg-muted/30 p-4">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <p className="text-xs text-muted-foreground">
                    {t('scan.create.preview.showing', { count: queryPreview.messages.length })}
                  </p>
                  <div className="flex items-baseline gap-2">
                    <span className="text-sm font-medium">{t('scan.create.preview.total')}</span>
                    <span className="text-sm font-semibold">{queryPreview.total}</span>
                  </div>
                </div>

                {queryPreview.messages.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    {t('scan.create.preview.empty')}
                  </p>
                ) : (
                  queryPreview.messages.map((message, index) => (
                    <article
                      key={`${message.url || message.title || 'message'}-${index}`}
                      className="flex items-start justify-between gap-3 rounded border-b bg-background px-3 py-2"
                    >
                      <div className="min-w-0 flex-1">
                              <p className="truncate text-sm font-medium">
                                {message.title
                                  ? highlightCoostoMatches(message.title)
                                  : t('scan.create.preview.untitled')}
                        </p>
                        {typeof message.author === 'string' && message.author && (
                          <p className="truncate text-xs text-muted-foreground">
                            {message.author}
                          </p>
                        )}
                        {message.publishedAt !== null &&
                          message.publishedAt !== undefined && (
                            <p className="mt-1 text-xs text-muted-foreground">
                              {new Date(message.publishedAt).toLocaleString('nl-NL')}
                            </p>
                          )}
                        <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">
                                {highlightCoostoMatches(message.text)}
                        </p>
                      </div>
                      {message.sourceType && (
                        <span className="shrink-0 text-sm text-muted-foreground">
                          {message.sourceType}
                        </span>
                      )}
                    </article>
                  ))
                )}
              </div>
            )}
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}

          {/* Actions */}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
              className="mt-2 md:mt-0"
            >
              {t('algemeen.annuleren')}
            </Button>
            <Button
              type="submit"
              variant="gradient"
              disabled={!isFormValid || isSubmitting}
              loading={isSubmitting}
            >
              {isSubmitting ? t('scan.create.actions.starting') : t('scan.create.actions.start')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
